import type {
  CatalogCardCatalogProps,
  CatalogCardCatalogPropsFlat,
  CatalogCardSupplierProps,
  CatalogCardSupplierPropsFlat,
  CatalogCardUserProps,
  CatalogCardUserPropsFlat,
} from '../types';

/**
 * Fusiona bloques agrupados con props planas (los bloques tienen prioridad).
 */
export function mergeCatalogCardCatalogProps(
  props: CatalogCardCatalogProps
): CatalogCardCatalogPropsFlat {
  const {
    media,
    content,
    tagRow,
    primaryCta,
    optionsMenu: optionsMenuBlock,
    primaryPanel,
    footer: footerBlock,
    ...flatIn
  } = props;

  const flat = flatIn as Omit<CatalogCardCatalogPropsFlat, 'title'> & { title?: string };

  const title =
    (content?.title ?? flat.title) !== undefined && (content?.title ?? flat.title) !== ''
      ? String(content?.title ?? flat.title)
      : '';

  if (process.env.NODE_ENV !== 'production' && title.trim() === '') {
    // eslint-disable-next-line no-console
    console.error(
      '[CatalogCard] Variante catálogo: indica `title` en la raíz o `content.title` dentro del bloque `content`.'
    );
  }

  return {
    className: flat.className,
    selected: flat.selected,
    emphasized: flat.emphasized,
    subtleBorder: flat.subtleBorder,
    to: flat.to,
    href: flat.href,
    cardLinkTarget: flat.cardLinkTarget,
    onClick: flat.onClick,
    disabled: flat.disabled,
    family: flat.family,
    layout: flat.layout,
    title,
    imageSrc: media?.imageSrc ?? flat.imageSrc,
    imageAlt: media?.imageAlt ?? flat.imageAlt,
    showExclusiveBadge: media?.showExclusiveBadge ?? flat.showExclusiveBadge,
    indicators: media?.indicators ?? flat.indicators,
    showAvailabilityIndicators:
      media?.showAvailabilityIndicators ?? flat.showAvailabilityIndicators,
    hideImage: media?.hideImage ?? flat.hideImage,
    categoryLabel: content?.categoryLabel ?? flat.categoryLabel,
    ratingValue: content?.ratingValue ?? flat.ratingValue,
    ratingTooltip: content?.ratingTooltip ?? flat.ratingTooltip,
    hideRating: content?.hideRating ?? flat.hideRating,
    locationLabel: content?.locationLabel ?? flat.locationLabel,
    locationHref: content?.locationHref ?? flat.locationHref,
    tags: tagRow?.tags ?? flat.tags,
    primarySelectedTags: tagRow?.primarySelectedTags ?? flat.primarySelectedTags,
    defaultPrimarySelectedTags:
      tagRow?.defaultPrimarySelectedTags ?? flat.defaultPrimarySelectedTags,
    onPrimaryTagsSelectionChange:
      tagRow?.onPrimaryTagsSelectionChange ?? flat.onPrimaryTagsSelectionChange,
    primaryTagsSelectAllLabel: tagRow?.primaryTagsSelectAllLabel ?? flat.primaryTagsSelectAllLabel,
    primaryTagsShowSelectAll: tagRow?.primaryTagsShowSelectAll ?? flat.primaryTagsShowSelectAll,
    primaryLinkHref: primaryCta?.primaryLinkHref ?? flat.primaryLinkHref,
    primaryLinkTo: primaryCta?.primaryLinkTo ?? flat.primaryLinkTo,
    primaryLinkLabel: primaryCta?.primaryLinkLabel ?? flat.primaryLinkLabel,
    primaryLinkTarget: primaryCta?.primaryLinkTarget ?? flat.primaryLinkTarget,
    primaryVariant: primaryCta?.primaryVariant ?? flat.primaryVariant,
    primaryLabel: primaryCta?.primaryLabel ?? flat.primaryLabel,
    primaryIcon: primaryCta?.primaryIcon ?? flat.primaryIcon,
    onPrimaryClick: primaryCta?.onPrimaryClick ?? flat.onPrimaryClick,
    primaryDisabled: primaryCta?.primaryDisabled ?? flat.primaryDisabled,
    primaryBadge: primaryCta?.primaryBadge ?? flat.primaryBadge,
    optionsPressed: optionsMenuBlock?.optionsPressed ?? flat.optionsPressed,
    onOptionsClick: optionsMenuBlock?.onOptionsClick ?? flat.onOptionsClick,
    optionsTooltip: optionsMenuBlock?.optionsTooltip ?? flat.optionsTooltip,
    hideOptionsMenu: optionsMenuBlock?.hidden ?? flat.hideOptionsMenu,
    optionsMenuItems: optionsMenuBlock?.optionsMenuItems ?? flat.optionsMenuItems,
    primaryMenuItems: primaryPanel?.primaryMenuItems ?? flat.primaryMenuItems,
    primaryMenuFooter: primaryPanel?.primaryMenuFooter ?? flat.primaryMenuFooter,
    primaryMenuMaxHeightClass:
      primaryPanel?.primaryMenuMaxHeightClass ?? flat.primaryMenuMaxHeightClass,
    fullWidth: flat.fullWidth,
    hidePrimaryCta:
      primaryCta?.hidden === true ||
      flat.hidePrimaryCta === true ||
      footerBlock?.showPrimaryCta === false,
  };
}

export function mergeCatalogCardSupplierProps(
  props: CatalogCardSupplierProps
): CatalogCardSupplierPropsFlat {
  const { profile, contact, cta, optionsMenu: optionsMenuBlock, ...flatIn } = props;
  const flat = flatIn as CatalogCardSupplierPropsFlat & Record<string, unknown>;

  const name = profile?.name ?? flat.name ?? '';
  const subtitle = profile?.subtitle ?? flat.subtitle ?? '';
  const avatarInitials = profile?.avatarInitials ?? flat.avatarInitials ?? '';

  if (process.env.NODE_ENV !== 'production') {
    if (!name || !subtitle || !avatarInitials) {
      // eslint-disable-next-line no-console
      console.error(
        '[CatalogCard] Variante proveedor: indica `name`, `subtitle` y `avatarInitials` en la raíz o dentro de `profile`.'
      );
    }
  }

  return {
    className: flat.className,
    selected: flat.selected,
    emphasized: flat.emphasized,
    subtleBorder: flat.subtleBorder,
    to: flat.to,
    href: flat.href,
    cardLinkTarget: flat.cardLinkTarget,
    onClick: flat.onClick,
    disabled: flat.disabled,
    family: 'supplier',
    name,
    subtitle,
    avatarInitials,
    ratingValue: profile?.ratingValue ?? flat.ratingValue,
    statusLabel: profile?.statusLabel ?? flat.statusLabel,
    phone: contact?.phone ?? flat.phone,
    email: contact?.email ?? flat.email,
    primaryButtonLabel: cta?.primaryButtonLabel ?? flat.primaryButtonLabel,
    onPrimaryClick: cta?.onPrimaryClick ?? flat.onPrimaryClick,
    onOptionsClick: optionsMenuBlock?.onOptionsClick ?? flat.onOptionsClick,
    optionsMenuItems: optionsMenuBlock?.optionsMenuItems ?? flat.optionsMenuItems,
  };
}

export function mergeCatalogCardUserProps(props: CatalogCardUserProps): CatalogCardUserPropsFlat {
  const { profile, contact, cta, optionsMenu: optionsMenuBlock, ...flatIn } = props;
  const flat = flatIn as CatalogCardUserPropsFlat & Record<string, unknown>;

  const name = profile?.name ?? flat.name ?? '';
  const subtitle = profile?.subtitle ?? flat.subtitle ?? '';
  const avatarInitials = profile?.avatarInitials ?? flat.avatarInitials ?? '';
  const avatarImageSrc = profile?.avatarImageSrc ?? flat.avatarImageSrc;
  const avatarImageAlt = profile?.avatarImageAlt ?? flat.avatarImageAlt;

  if (process.env.NODE_ENV !== 'production') {
    if (!name || !subtitle || !avatarInitials) {
      // eslint-disable-next-line no-console
      console.error(
        '[CatalogCard] Variante user: indica `name`, `subtitle` y `avatarInitials` en la raíz o dentro de `profile`.'
      );
    }
  }

  return {
    className: flat.className,
    selected: flat.selected,
    emphasized: flat.emphasized,
    subtleBorder: flat.subtleBorder,
    to: flat.to,
    href: flat.href,
    cardLinkTarget: flat.cardLinkTarget,
    onClick: flat.onClick,
    disabled: flat.disabled,
    family: 'user',
    name,
    subtitle,
    avatarInitials,
    avatarImageSrc,
    avatarImageAlt,
    statusLabel: profile?.statusLabel ?? flat.statusLabel,
    phone: contact?.phone ?? flat.phone,
    email: contact?.email ?? flat.email,
    primaryButtonLabel: cta?.primaryButtonLabel ?? flat.primaryButtonLabel,
    onPrimaryClick: cta?.onPrimaryClick ?? flat.onPrimaryClick,
    onOptionsClick: optionsMenuBlock?.onOptionsClick ?? flat.onOptionsClick,
    optionsMenuItems: optionsMenuBlock?.optionsMenuItems ?? flat.optionsMenuItems,
  };
}
